// Blog → New post. M5, revised 2026-08-15.
//
// A dedicated page rather than an inline panel on the list (owner request).
// Deliberately minimal: title, language, slug. Everything else — body, excerpt,
// category, images, publishing — happens in the editor once the row exists,
// because the upload-first image endpoint needs somewhere to attach and
// publishing needs a body to publish.
//
// ⚠️ The slug auto-fills from the title HERE ONLY. Once a post exists, editing
// its title must never silently change its URL and 404 every published link.

import { Button, Card, Field, Input } from '@fajrarabi/ui';
import { blogSlug, slugifyBlog } from '@fajrarabi/zod-schemas/blog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import { createPost, type BlogLocale } from '~/lib/blog';

export const Route = createFileRoute('/_authed/blog/new')({
  component: NewBlogPostPage,
});

function NewBlogPostPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [languageCode, setLanguageCode] = useState<BlogLocale>('ar');
  // Empty means "follow the title". Once the user edits it, we stop overwriting.
  const [slugOverride, setSlugOverride] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const slug = slugOverride ?? slugifyBlog(title);
  const slugValid = slug !== '' && blogSlug.safeParse(slug).success;
  const dir = languageCode === 'ar' ? 'rtl' : 'ltr';

  const createMut = useMutation({
    mutationFn: () => createPost({ title: title.trim(), slug, languageCode, isPublished: false }),
    onSuccess: ({ id }) => {
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      // Straight into the editor — the draft has no body yet.
      void navigate({ to: '/blog/$id', params: { id } });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create the post.');
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <header>
        <Button variant="ghost" onClick={() => void navigate({ to: '/blog' })}>
          ← All posts
        </Button>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">New post</h1>
        <p className="mt-1 text-sm text-slate-600">
          Creates a draft. Nothing is publicly visible until you publish it in the editor.
        </p>
      </header>

      {error && (
        <p role="alert" className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </p>
      )}

      <Card className="mt-6">
        <div className="p-4">
          <Field
            label="Language"
            hint="Decides which listing the post appears in. A post lives in one language only — write it twice to publish in both."
          >
            {(fieldId) => (
              <select
                id={fieldId}
                value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value as BlogLocale)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
              >
                <option value="ar">Arabic — shows on /blog</option>
                <option value="en">English — shows on /en/blog</option>
              </select>
            )}
          </Field>

          <Field label="Title" className="mt-4">
            {(fieldId) => (
              <Input
                id={fieldId}
                dir={dir}
                value={title}
                autoFocus
                placeholder={languageCode === 'ar' ? 'عنوان المقال' : 'Post title'}
                onChange={(e) => setTitle(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Slug"
            className="mt-4"
            hint="Filled in from the title. Arabic titles keep Arabic slugs; diacritics are stripped."
            error={
              title.trim() && !slugValid
                ? 'Could not derive a valid slug from that title.'
                : undefined
            }
          >
            {(fieldId) => (
              <Input
                id={fieldId}
                dir="auto"
                value={slug}
                onChange={(e) => setSlugOverride(e.target.value)}
              />
            )}
          </Field>

          {slugValid && (
            <p className="mt-2 text-xs text-slate-500">
              URL:{' '}
              <code className="rounded bg-slate-100 px-1">
                /{languageCode === 'en' ? 'en/' : ''}blog/{slug}
              </code>
            </p>
          )}

          <div className="mt-6 flex gap-2">
            <Button
              onClick={() => createMut.mutate()}
              disabled={!title.trim() || !slugValid || createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create draft'}
            </Button>
            <Button variant="ghost" onClick={() => void navigate({ to: '/blog' })}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
